import axios from 'axios';
import applyInterceptors from '../interceptors';
import { API_KEYS } from '../common/consts';
import { RequestError } from '../common/errors';
import OLContentClient from './OLContentClient';
import OLResultsClient from './OLResultsClient';
import OLResultsClientWithKeycloak from './OLResultsClientWithKeycloak';

const axiosInstance = axios.create();
applyInterceptors(axiosInstance);

const contentClient = new OLContentClient(axiosInstance);
const resultsClient = new OLResultsClient(axiosInstance);
const olsResultsClientWithKeycloak = new OLResultsClientWithKeycloak(axiosInstance);

const assessment = 'OL';

/**
 * @param {(String)[]}  studentUids    - Array of students uids
 * @param {String}      schoolYear     - School year, optional parameter. (E.g. for 18/19 it is 2019)
 * @param {Array<String>} [language] - results' language, e.g. ['eng', 'spa']
 * @param {String=}     classId        - Class id, optional parameter
 *
 * @return {Promise<{Object[] >}} student results
 */
export const getOLResults = async (studentUids, schoolYear, language, classId) => {
    try {
        return await resultsClient.getResults(studentUids, schoolYear, language, classId);
    } catch (error) {
        throw new RequestError(`${assessment} get results request failed`, error, assessment);
    }
};

/**
 * @param {(String)[]}  studentUids      - Array of students uids
 * @param {String}      [schoolYear]     - School year, optional parameter.
 * @param {String=}     classId          - Class id, optional parameter.
 *
 * @param {String}      instId If set, this field will enable support of custom gating rules
 * @param {String}      token            - keycloak token for API authorization
 * @param {String}      staffId          - staffId for an additional header used in results caching.
 * @param language
 * @return {Promise<{results: Object[], studentStatuses: Object[]}>}      - Results from Results API
 */
export const getOLSResultsAndStatuses = async (
    studentUids,
    schoolYear,
    classId,
    instId,
    token,
    staffId = '',
    language,
) => {
    try {
        return await olsResultsClientWithKeycloak.getResults(
            studentUids,
            schoolYear,
            classId,
            instId,
            token,
            staffId,
            language,
        );
    } catch (error) {
        throw new RequestError(`${assessment} get results request failed`, error, assessment);
    }
};

/**
 * Save OL results to API.
 * Decide between update and create operations based on `resultId` presence.
 * Handle non-fatal errors (404 & 409). Reject with first met fatal errror.
 * @param {Array<Object>} results result objects to be saved
 * @return {Promise<{Object[] >}} server responses per result operation or error
 */
export const persistOLResults = async results => {
    try {
        const response = await resultsClient.persistResults(results);
        if (Array.isArray(response)) {
            return response.map(resultResponse =>
                resultResponse instanceof Error
                    ? new RequestError(
                          `${assessment} persist result request failed`,
                          resultResponse,
                          assessment,
                      )
                    : resultResponse,
            );
        }
        throw new Error(`Incorrect response format: ${response}`);
    } catch (error) {
        throw new RequestError(`${assessment} results saving failed`, error, assessment);
    }
};

/**
 * Retrieve assessment content from API: forms and cutscores
 * @return {Promise<{ forms: Object[], cutscores: Object[] }>} OL content
 */
export const getOLContent = async language => {
    try {
        const [forms, cutscores] = await Promise.all([
            contentClient.getForms(language),
            contentClient.getCutscores(language),
        ]);

        return {
            [API_KEYS.FORMS]: forms,
            [API_KEYS.CUTSCORES]: cutscores,
        };
    } catch (error) {
        throw new RequestError(`${assessment} get content request failed`, error, assessment);
    }
};

/**
 * @param {String} token - keycloak token for API authorization
 * @param {Array[String]} languages - language spa, eng
 * @returns {Promise<Object>} OLS forms
 */
export const getOLSForms = async (token, languages) => {
    let forms;
    try {
        forms = await contentClient.getForms(languages, token);
    } catch (error) {
        throw new RequestError(`${assessment} get forms request failed`, error, assessment);
    }

    return forms.map(item => ({
        ...item,
        'uid': item.formUid,
    }));
};

/**
 * @param {String} token - keycloak token for API authorization
 * @param {Array[String]} languages - language spa, eng
 * @returns {Promise<Object>} OLS cutscores
 */
export const getOLSCutscores = async (token, languages) => {
    let cutscores;
    try {
        cutscores = await contentClient.getCutscores(languages, token);
    } catch (error) {
        throw new RequestError(`${assessment} get cutscores request failed`, error, assessment);
    }

    return cutscores || [];
};

export const persistOLSResultsWithKeycloak = async (results, token) => {
    try {
        olsResultsClientWithKeycloak.setToken(token);
        const response = await olsResultsClientWithKeycloak.persistResults(results);
        if (Array.isArray(response)) {
            return response.map(resultResponse =>
                resultResponse instanceof Error
                    ? new RequestError(
                          `${assessment} persist result request failed`,
                          resultResponse,
                          assessment,
                      )
                    : resultResponse,
            );
        }
        throw new Error(`Incorrect response format: ${response}`);
    } catch (error) {
        throw new RequestError(`${assessment} results saving failed`, error, assessment);
    }
};

export { OLContentClient, OLResultsClient, OLResultsClientWithKeycloak, applyInterceptors };
